import { Box, TextField, InputAdornment, CircularProgress, Alert, AlertTitle } from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { getHeader } from "../service/auth";
import FormDatePicker from "./FormDatePicker";
import dayjs from "dayjs";
import { FormInputRadio } from "./FormInputRadio";
import SnackAlert from "./SnackAlert";
import themeNutriFast from "../styles/NutriFastColors";
import { motion } from 'framer-motion';
import { AccountCircle, Email, Phone, Person, CheckCircle, Cancel } from "@mui/icons-material";
import useRetryableApi from "../hooks/useRetryableApi";

const major = dayjs().subtract(16, 'year');

const ProfilForm = ({ defaultValues, admin, onProfileUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const headers = getHeader();
  const { executeWithRetry, getErrorMessage } = useRetryableApi();
  
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "info"
  });

  const {
    handleSubmit,
    setValue,
    reset,
    control,
    register,
    formState: { errors, isValid, touchedFields },
    watch,
  } = useForm({
    mode: "onSubmit", // Validation à la soumission, pas de blocage
    defaultValues: {
      username: "",
      mail: "",
      telephone: "",
      sexe: 1, // 0 = femme, 1 = homme
      birth_date: dayjs(),
      admin: false,
      id: ""
    }
  });

  // Watch pour validation temps réel
  const watchUsername = watch("username");
  const watchMail = watch("mail");
  const watchTelephone = watch("telephone");

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlert({ ...alert, open: false });
  };

  useEffect(() => {
    if (defaultValues && (defaultValues.username || defaultValues.name)) {
      reset({
        username: defaultValues.username || defaultValues.name,
        mail: defaultValues.mail || "",
        telephone: defaultValues.telephone || "",
        sexe: defaultValues.sexe !== undefined ? defaultValues.sexe : 1,
        birth_date: dayjs(defaultValues.birth_date),
        admin: defaultValues.admin || false,
        id: defaultValues.id || ""
      });
    }
  }, [defaultValues, reset]);

  const onSubmit = async (data) => {
    console.log("🔥 onSubmit appelé avec data:", data);
    console.log("admin:", admin);
    setLoading(true);
    setServerError("");

    // Convertir birth_date de Dayjs vers ISO string pour Sequelize
    const preparedData = {
      ...data,
      birth_date: data.birth_date?.format ? data.birth_date.format('YYYY-MM-DD') : data.birth_date
    };

    // Requête différente selon si admin édite un utilisateur ou utilisateur édite son profil
    const apiCall = admin && preparedData.id
      ? () => axios.put(`${process.env.REACT_APP_ADMIN}/user/${preparedData.id}`, { data: preparedData }, { headers })
      : () => axios.post(process.env.REACT_APP_USER_URL, { data: preparedData }, { headers });

    console.log("📡 URL de la requête:", admin && preparedData.id ? `PUT ${process.env.REACT_APP_ADMIN}/user/${preparedData.id}` : `POST ${process.env.REACT_APP_USER_URL}`);

    await executeWithRetry(
      apiCall,
      () => {
        setAlert({
          message: admin ? 'Profil utilisateur mis à jour' : 'Vos données ont été mises à jour',
          severity: 'success',
          open: true
        });
        setLoading(false);

        // Recharger les données du profil après mise à jour réussie
        if (onProfileUpdate) {
          onProfileUpdate();
        }
      },
      (error, attemptCount) => {
        setServerError(getErrorMessage(error, attemptCount));
        setAlert({
          message: getErrorMessage(error, attemptCount),
          severity: 'error',
          open: true
        });
        setLoading(false);
      }
    );
  };

  // Fonction pour déterminer le statut des champs
  const getFieldStatus = (fieldName, value, condition) => {
    if (!touchedFields[fieldName]) return null;
    if (errors[fieldName]) return "error";
    if (condition) return "success";
    return null;
  };

  const getFieldIcon = (fieldName, value, condition) => {
    const status = getFieldStatus(fieldName, value, condition);
    if (status === "success") return <CheckCircle color="success" />;
    if (status === "error") return <Cancel color="error" />;
    return null;
  };

  let buttons = [
    { value: true, label: "Administrateur" },
    { value: false, label: "Volontaire" },
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 2 }}>
      <Box sx={{ textAlign: "center", maxWidth: 600, mx: "auto", p: 3 }}>
        <h2 style={{ color: themeNutriFast.palette.primary.dark }} className="title-dashbord">
          Profil
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="form">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            
            <TextField
              fullWidth
              label="Pseudo"
              variant="outlined"
              disabled={loading}
              {...register("username", {
                required: "Le pseudo est obligatoire",
                minLength: {
                  value: 3,
                  message: "Le pseudo doit contenir au moins 3 caractères",
                },
              })}
              error={!!errors.username}
              helperText={errors.username?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircle />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {getFieldIcon("username", watchUsername, watchUsername?.length >= 3)}
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              type="email"
              label="Adresse email"
              variant="outlined"
              disabled={loading}
              {...register("mail", {
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Format d'email invalide",
                },
              })}
              error={!!errors.mail}
              helperText={errors.mail?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {getFieldIcon("mail", watchMail, !watchMail || /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(watchMail))}
                  </InputAdornment>
                ),
              }}
            />


            <TextField
              fullWidth
              label="Téléphone"
              variant="outlined"
              disabled={loading}
              {...register("telephone", {
                pattern: {
                  value: /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/,
                  message: "Format de téléphone invalide",
                },
              })}
              error={!!errors.telephone}
              helperText={errors.telephone?.message || "Format: 06 12 34 56 78"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {getFieldIcon("telephone", watchTelephone, !watchTelephone || /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/.test(watchTelephone))}
                  </InputAdornment>
                ),
              }}
            />

            <FormDatePicker
              name="birth_date"
              views={["month", "year"]}
              label="Date de naissance"
              control={control}
              maxDate={major}
              rules={{
                required: "La date de naissance est obligatoire",
              }}
            />

            <FormInputRadio
              label="Sexe"
              name="sexe"
              buttons={[
                { value: 1, label: "Homme" },
                { value: 0, label: "Femme" },
              ]}
              control={control}
              rules={{}}
              xs={12}
            />

            {admin && (
              <Box>
                <input type="hidden" {...register("id")} />
                <FormInputRadio
                  label="Rôle"
                  name="admin"
                  buttons={buttons}
                  control={control}
                  rules={{}}
                  xs={12}
                />
              </Box>
            )}

            {serverError && (
              <Alert severity="error">
                <AlertTitle>{serverError}</AlertTitle>
              </Alert>
            )}

            <button
              className="button btn-nutrifast"
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={loading}
              style={{
                margin: "auto",
                width: "100%",
                backgroundColor: themeNutriFast.palette.primary.dark,
                position: "relative",
              }}
            >
              {loading ? (
                <>
                  <CircularProgress
                    size={20}
                    color="inherit"
                    style={{ marginRight: 8 }}
                  />
                  Mise à jour en cours...
                </>
              ) : (
                admin ? "Mettre à jour le profil" : "Mettre à jour mon profil"
              )}
            </button>
          </Box>
        </form>

        <SnackAlert
          open={alert.open}
          severity={alert.severity}
          message={alert.message}
          handleClose={handleClose}
        />
      </Box>
    </motion.div>
  );
};

export default ProfilForm;